/**
 * Curated propagation maps for Atlas Day 2 Phase 6 Layer 8 inspect content.
 *
 * Per scope §6 layer 8: hybrid (c) categorical default + (a) curated map
 * for high-stakes ownership-altitude values. Each map carries a "verified
 * as of [build day]" staleness marker per scope doc §8 + Convention 35
 * (no-stale-citations).
 *
 * Day 2 P6 ships curated maps for 2 worked-example sub-regions
 * (per-distributor + business-defaults). Atlas Day 2.5 follow-up extends
 * to remaining 6 sub-regions.
 *
 * Verification reference for staleness marker: 2026-05-27 (Atlas Day 2
 * P0 consumer enumeration per #12 = 23 useSettingsConfig/useParamStore
 * consumer files; per #13 = 5 runtime bswc consumers).
 */

export interface CuratedPropagationEntry {
  field: string;
  affects: string[];
  /** Last-verified build day or session marker */
  verifiedAt: string;
}

/**
 * Per-distributor propagation per scope §6.1.1 L8 (categorical entry per
 * scope content).
 */
export const PER_DISTRIBUTOR_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'credit_limit / payment_terms',
    affects: [
      'Credit gate evaluation at PO submission',
      'Finance → Credit & Cash dashboard',
      'useApp distributor context refresh on edit (Day 84.5 M1-a SEED-ONCE pattern)',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2 P0)',
  },
  {
    field: 'is_active',
    affects: [
      'Buying session distributor selector visibility',
      'PO Builder distributor filter',
      'Transfers distributor selector',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2 P0)',
  },
];

/**
 * Per-business-defaults propagation per scope §6.2 L8 — hybrid (c)
 * categorical default for most params + (a) curated maps for Engine
 * Tuning subsection + key strategic params.
 */
export const BUSINESS_DEFAULTS_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'min_roi_pct',
    affects: [
      'BuyingSession Buy Score gating (engine LEFT JOIN bswc applies_to_metric=roi_pct)',
      'Title block sentence 1 ROI clause',
      'ProfitabilityDetail UI rendering',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2 P0)',
  },
  {
    field: 'returns_reserve_pct',
    affects: [
      'calculate_profitability RPC returns reserve calculation',
      '23 useSettingsConfig consumer files (per Phase 0 #12 verified-current rollup)',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2 P0)',
  },
  {
    field: 'default_lead_time_days',
    affects: [
      'forecast_restock RPC lead-time fallback when supplier lead time absent',
      'Restock page urgency tier calculation',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2 P0)',
  },
];

// ── Atlas Day 2.5 P2 extensions ────────────────────────────────────────────
// Staleness markers: 2026-05-27 (Atlas Day 2.5 P0 #6 re-verified Day 60b
// consumer rollup unchanged at 23 files; bswc consumers unchanged).

/**
 * Per-supplier propagation per scope §6.1.2 L8.
 * Substrate currently empty (Phase 0 #2 verified N=0); maps shape
 * anticipates population at supplier-onboarding altitude.
 */
export const PER_SUPPLIER_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'lead_time_days',
    affects: [
      'forecast_restock RPC velocity-resolution chain when supplier lead time present',
      'PO Builder lead-time-aware quantity recommendations',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
  {
    field: 'moq',
    affects: [
      'PO Builder minimum-order-quantity validation at PO line item add',
      'BuyingSession quantity-step affordance per supplier moq when surfaced',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
  {
    field: 'is_active',
    affects: [
      'Supplier dropdowns in PO Builder + Directory → Suppliers',
      'Inactive suppliers filtered from active-supplier counts in cluster cards',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
];

/**
 * Per-brand propagation per scope §6.1.3 L8 (post-scope-doc-clarification
 * commit 2868958 — cross-table MAP semantics surface at MAP enforcement
 * rule region inspect, not per-brand inspect).
 */
export const PER_BRAND_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'enforces_map',
    affects: [
      'calculate_profitability below_map evaluation (cross-table with products.map_price per scope §6.6.1)',
      'BuyingSession MAP warning chip visibility (MapWarningChip component)',
      'ProductDetail MAP enforcement display',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
  {
    field: 'is_active',
    affects: [
      'BuyingSession brand filter dropdown',
      'Directory → Brands active filter',
      'Active brand counts in PerBrandCard surface (Atlas Day 2 P3)',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
  {
    field: 'return_policy / restocking_fee_pct',
    affects: [
      'Returns flow per-brand policy lookup',
      'Finance restocking-fee per-brand application',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
  {
    field: 'marketplace_permissions',
    affects: [
      'Day 90 3-tier marketplace-permissions hierarchy (per RBP L956-970)',
      'Cross-domain permission stance per brand',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
];

/**
 * Per-manufacturer propagation per scope §6.1.4 L8.
 * Substrate sparse (Phase 0 #4: 1 manufacturer / 1 pairing).
 */
export const PER_MANUFACTURER_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'is_active',
    affects: [
      'Manufacturer dropdowns in Directory → Manufacturers + Brand edit form',
      'Active manufacturer counts in PerManufacturerCard surface',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
  {
    field: 'manufacturer_distributors pairings',
    affects: [
      'Path eligibility rule region (Atlas Day 3 §6.6.3) — pairings gate which fulfillment paths are calculable per product',
      'PO Builder path filter when manufacturer-distributor pairing absent',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
];

/**
 * Per-category propagation per scope §6.1.5 L8.
 * Substrate currently empty (Phase 0 #5 verified N=0; substrate populates
 * per Day 88 CF 64 expansion). Maps shape anticipates Day 88 substrate.
 */
export const PER_CATEGORY_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'is_active',
    affects: [
      'Category dropdowns at Directory → Categories (when populated)',
      'BuyingSession category filter (when category substrate ships)',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
  {
    field: 'name',
    affects: [
      'Per-category overrides at Day 88 CF 64 substrate extension',
      'Taxonomic structure surface in ProductDetail',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
];

/**
 * Path mechanics propagation per scope §6.3 L8 — most heavily-populated
 * map per Day 60b consumer rollup (23 useSettingsConfig/useParamStore
 * consumer files verified Atlas Day 2 P0 #12 + re-verified Atlas Day 2.5
 * P0 #6).
 */
export const PATH_MECHANICS_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'is_active',
    affects: [
      'calculate_profitability path filter at engine evaluation',
      '23 useSettingsConfig consumer files (per Phase 0 #12 + #6 verified-current rollup)',
      'Path eligibility rule region (Atlas Day 3 §6.6.3) — active paths gate calculable channel-path combinations',
      'Settings → Channels & Paths is_active toggle visibility',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
  {
    field: 'path_type',
    affects: [
      'Engine path branching by merchant_managed / fulfillment_service_managed / dropship enum (Day 86 A3 substrate)',
      'fulfillment_path_config metadata flag interpretation in engine RPCs',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
  {
    field: 'metadata flags (applies_inbound_shipping / applies_prep_fee / payout_param_key)',
    affects: [
      'calculate_profitability per-path cost branching (Day 66 P2.1 substrate)',
      'POBuilder + PODetail per-path cost display (BACKLOG F24 candidate engine-duplicate-logic retirement at Day 85+ standalone)',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
];

/**
 * Buy Score weight components propagation per scope §6.4 L8.
 * Consumes Atlas Day 2 P0 #13 enumeration: 5 runtime bswc consumers
 * (Engine Editor → Weight Components admin + useSettingsConfig data layer + bswcTooltipFallback
 * function + buyingSessionDefaults + BuyingSession via tooltip fallback) +
 * Atlas Day 1 titleBlockSubstrate (6th, Atlas itself). Atlas Day 2.5
 * WeightComponentsInspect = 7th if it queries bswc directly (canonical
 * pattern from PerDistributorInspect data-driven shape).
 */
export const WEIGHT_COMPONENTS_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'is_available',
    affects: [
      'calculate_profitability bswc LEFT JOIN engine catalog admission (Day 68 P3.1 Q1 Path B)',
      'Engine Editor → Weight Components → is_available column (Day 2.5)',
      'BuyingSession header tooltip surface (via bswcTooltipFallback)',
      'Atlas Weight components region grid card active count (Day 2 P4)',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
  {
    field: 'applies_to_metric',
    affects: [
      'calculate_profitability CASE dispatch per bswc.applies_to_metric (Day 68 P3.1 Q1 Path B)',
      'F33 trap detection (is_available=true AND applies_to_metric IS NULL → engine silent zero)',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
  {
    field: 'tooltip_text',
    affects: [
      'BuyingSession header tooltip 4-step fallback chain step 1 (Day 91a.5 P2D)',
      'bswcTooltipFallback resolveTooltip consumer surfaces (current count: BuyingSession=1, Atlas Day 2.5 WeightComponentsInspect=2; Template 18 promotion waits per BACKLOG L70 D-1 lock)',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
  {
    field: 'penalty',
    affects: [
      'calculate_profitability per-metric weight negation in buy_score calculation (Day 68 P3.1)',
      'Engine Editor → Weight Components → penalty column (Day 2.5)',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 2.5 P0)',
  },
];

// ── Atlas Day 3a P2 transformation-row extensions ──────────────────────────
// Staleness markers: 2026-05-27 (Atlas Day 3a P0 verifications + Day 60b
// consumer rollup unchanged at 23 files since Day 2.5).

/**
 * Engine calculations propagation per scope §6.5 L8 — region-scope
 * deployment-not-cache. Per-RPC entries reference Days 68-72 refactor
 * commits + RPC consumer enumeration.
 */
export const ENGINE_CALCULATIONS_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'Engine RPC bodies (calculate_profitability + 4 siblings)',
    affects: [
      'Deployment-not-cache: code changes ship via Vercel + supabase db push; downstream surfaces consume new behavior immediately on deploy',
      'Days 68-72 refactor scope: calculate_profitability (Day 68 P3.1) + calculate_profitability_tiers (Day 69 P3.2) + calculate_breakdown_extras (Day 70 P3.3) + forecast_restock (Day 71 P3.4) + calculate_po_impact (Day 72 P3.5)',
      'Downstream surfaces: BuyingSession profitability columns + ProductDetail profitability section + POBuilder cost preview + Restock urgency calculation + Reports profitability rollups',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 3a P0 #8)',
  },
  // Fix Pass B (2026-06-09) wired the manufacturer↔distributor discount into the
  // cost chain — a NEW live engine input, surfaced here per the cost-chain
  // content-add owed since that build (pre-audit clearance 2026-06-10).
  {
    field: 'manufacturer_distributors.discount_pct (cost-of-goods input)',
    affects: [
      'cost_product = cost × (1 − discount) in all four cost RPCs (calculate_profitability + _tiers + _breakdown_extras + calculate_po_impact); goods-only — never multiplies shipping / prep / misc; stacks on the tier unit_cost in tier mode',
      'Downstream: profit / ROI / margin on every fulfillment path → buy decisions (min_roi / min_margin gates) → Buy Score (ROI-derived metric arms) → POBuilder cost preview + PO impact',
      'Edit home: Directory → Manufacturers (per-distributor discount junction rows); a product with no manufacturer link or 0% discount resolves to unchanged cost',
    ],
    verifiedAt:
      '2026-06-10 (pre-audit clearance 1; wiring proven by scripts/verify-stage3-fixB.cjs 7/0 — 20% discount → cost_product ×0.8 exactly)',
  },
];

/**
 * MAP enforcement propagation per scope §6.6.1 L8 (post scope-doc
 * clarification commit 2868958). Cross-table cascade: brands.enforces_map
 * + products.map_price → below_map evaluation.
 */
export const MAP_ENFORCEMENT_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'brands.enforces_map',
    affects: [
      'calculate_profitability below_map evaluation (cross-table: brands.enforces_map AND products.map_price IS NOT NULL AND sale_price < products.map_price)',
      'BuyingSession MAP warning chip (MapWarningChip component)',
      'ProductDetail MAP enforcement display + warning surface',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 3a P0 #12)',
  },
  {
    field: 'products.map_price',
    affects: [
      'calculate_profitability below_map arithmetic input (sale_price < products.map_price)',
      'Algopix import sets products.map_price + writes products.provenance trace',
      'Future: CF 63 repricer consumer when MAP-aware repricing ships',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 3a P0 #12)',
  },
];

/**
 * Credit gate propagation per scope §6.6.2 L8 — D-1 lock 2026-05-27:
 * credit_ledger actual columns (event_type + notes + balance_after + po_id
 * + amount + distributor_id + created_by + created_at).
 */
export const CREDIT_GATE_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'distributors.credit_limit / payment_terms',
    affects: [
      'PO submission credit-gate evaluation (whether PO total + outstanding exceeds credit_limit)',
      'Finance → Credit & Cash dashboard credit-utilization-percent calculations',
      'useApp distributor context refresh on edit (Day 84.5 M1-a SEED-ONCE pattern)',
      'Title block sentence 2 "BLVS on net-30 terms" credit posture surface',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 3a P0)',
  },
  {
    field: 'credit_ledger entries (event_type + amount + balance_after)',
    affects: [
      'PO submission creates credit_ledger entries linked via po_id; cascade visible in Finance → Credit & Cash dashboard',
      'credit_ledger entries carry balance_after running state (immutable append-only per scope §6.6.2 L9 reversibility)',
      'event_type field tags entry kind (PO_SUBMITTED, PO_PAID, etc.); notes field captures contextual annotation',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 3a P0 #9 D-1 lock)',
  },
];

/**
 * Path eligibility propagation per scope §6.6.3 L8. Engine-layer JOIN per
 * Phase 0 #11 refinement: fpc × marketplaces (not manufacturer_distributors
 * at engine altitude).
 */
export const PATH_ELIGIBILITY_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'fulfillment_path_config.is_active × marketplaces.is_active',
    affects: [
      'calculate_profitability path filter at engine evaluation (fpc.is_active = true AND fpc.marketplace_id matches active marketplace)',
      'product_profitability cache rows: only active path × channel combinations populate',
      'BuyingSession calculable channel-path enumeration',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 3a P0 #11)',
  },
  {
    field: 'manufacturer_distributors pairings (gate-altitude)',
    affects: [
      'NOT engine-layer JOIN — gate-altitude judgment per scope §6.6.3 L4',
      'Manufacturer-distributor network membership constrains which fulfillment paths are calculable per product (via product.manufacturer_id constraint)',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 3a P0 #11)',
  },
];

/**
 * Restock urgency tiers propagation per scope §6.6.4 L8 — D-2 lock
 * 2026-05-27: RPC-default substrate model. Multipliers via COALESCE-fallback
 * in forecast_restock + siblings (defaults 1.0 + 1.5; NOT DB-stored at
 * current state).
 */
export const RESTOCK_URGENCY_TIERS_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'restock_urgency_red_lead_multiplier / restock_urgency_yellow_lead_multiplier (RPC defaults 1.0 + 1.5)',
    affects: [
      'forecast_restock RPC output: tightness-tier classification per-product (Red / Yellow / Healthy)',
      'Restock page urgency tier filtering + sort',
      'Dashboard restock pipeline strip urgency counts',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 3a P0 #10 D-2 lock)',
  },
];

/**
 * Buy Score composition propagation per scope §6.6.5 L8. Cross-references
 * WEIGHT_COMPONENTS_PROPAGATION (substrate × calculation cell) per scope
 * §6.6.5 framing to avoid duplication — Weight components region holds the
 * substrate-side propagation; Buy Score composition rule holds the
 * downstream-decision-side propagation.
 */
export const BUY_SCORE_COMPOSITION_PROPAGATION: CuratedPropagationEntry[] = [
  {
    field: 'Buy Score blend formula (calculate_profitability buy_score branch)',
    affects: [
      'calculate_profitability buy_score column populated per product × path × channel',
      'BuyingSession Buy Score grid column sort + filter',
      'Top-ranked product selection per buying-session scenario',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 3a P0)',
  },
  {
    field: 'Active bswc catalog (per scope §6.6.5 cross-ref Weight components substrate)',
    affects: [
      'See WEIGHT_COMPONENTS_PROPAGATION (substrate × calculation cell) for substrate-side propagation cascade',
      'Per-session buyer-private weighting in buying_sessions.ui_state.weights (D1 (α) lock — engine catalog only at Atlas)',
    ],
    verifiedAt: '2026-05-27 (Atlas Day 3a P0)',
  },
];
